import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, ListGroup, Spinner, Badge, InputGroup } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';

export default function BucketManager() {
    const api = useApi();

    // State
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedBucket, setSelectedBucket] = useState(null);

    // Form State
    const [editForm, setEditForm] = useState({
        name: '', goal: 0, archived: false,
        loanPrincipal: 0, loanInterest: 0, loanMinPayment: 0, loanStart: ''
    });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        const res = await api.get('/budget/categories');
        if (res.ok) {
            setCategories(res.body.items || []);
        }
        setIsLoading(false);
    };

    // Populate form when a bucket is clicked
    useEffect(() => {
        if (selectedBucket) {
            setEditForm({
                name: selectedBucket.name,
                goal: parseFloat(selectedBucket.goal || 0),
                archived: selectedBucket.archived || false,
                loanPrincipal: selectedBucket.loan_details ? selectedBucket.loan_details.original_principal : 0,
                loanInterest: selectedBucket.loan_details ? selectedBucket.loan_details.interest_rate : 0,
                loanMinPayment: selectedBucket.loan_details ? selectedBucket.loan_details.min_payment : 0,
                loanStart: selectedBucket.loan_details?.start_date || ''
            });
        }
    }, [selectedBucket]);

    const handleUpdateBucket = async (e) => {
        e.preventDefault();
        if (!selectedBucket) return;

        const payload = {
            name: editForm.name,
            goal: editForm.goal,
            archived: editForm.archived
        };

        // Only send loan updates if this bucket is actually a loan
        if (selectedBucket.loan_details) {
            payload.loan_details = {
                original_principal: editForm.loanPrincipal,
                interest_rate: editForm.loanInterest,
                min_payment: editForm.loanMinPayment,
                start_date: editForm.loanStart
            };
        }

        const res = await api.put(`/budget/buckets/${selectedBucket.id}`, payload);

        if (res.ok) {
            // Update UI instantly without a full reload
            setCategories(prev => prev.map(cat => ({
                ...cat,
                buckets: cat.buckets.map(b => b.id === selectedBucket.id ? res.body : b)
            })));
            setSelectedBucket(res.body);
            alert("Bucket updated successfully!");
        } else {
            alert(res.body?.message || "Failed to update bucket.");
        }
    };

    return (
        <Container className="py-4">
            <h2 className="fw-bold mb-4"><i className="bi bi-archive text-primary me-2"></i> Master Bucket Manager</h2>

            <Row className="g-4">
                {/* LEFT COLUMN: CATEGORY & BUCKET LIST */}
                <Col md={5} lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                        <div className="p-3 border-bottom bg-light">
                            <h6 className="fw-bold text-uppercase letter-spacing-1 mb-0 text-muted">Your Envelopes</h6>
                        </div>

                        <div className="overflow-auto" style={{ maxHeight: '700px' }}>
                            {isLoading ? (
                                <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
                            ) : (
                                categories.map(cat => (
                                    <div key={cat.id} className="mb-1">
                                        <div className="bg-light bg-opacity-50 px-3 py-2 border-bottom border-top small fw-bold text-header-caps text-muted">
                                            {cat.name}
                                        </div>
                                        <ListGroup variant="flush">
                                            {cat.buckets.map(b => (
                                                <ListGroup.Item
                                                    key={b.id}
                                                    action
                                                    active={selectedBucket?.id === b.id}
                                                    onClick={() => setSelectedBucket(b)}
                                                    className="d-flex justify-content-between align-items-center py-3 border-0"
                                                >
                                                    <div>
                                                        <span className={`fw-medium ${b.archived ? 'text-decoration-line-through text-muted' : ''}`}>
                                                            {b.name}
                                                        </span>
                                                        {b.loan_details && <Badge bg="warning" className="ms-2" text="dark">LOAN</Badge>}
                                                    </div>
                                                    <i className="bi bi-chevron-right small opacity-50"></i>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </div>
                                ))
                            )}
                        </div>
                    </Card>
                </Col>

                {/* RIGHT COLUMN: EDIT FORM */}
                <Col md={7} lg={8}>
                    {selectedBucket ? (
                        <Card className="border-0 shadow-sm rounded-4 h-100 animation-fade-in">
                            <Card.Body className="p-4 p-md-5">
                                <div className="mb-4">
                                    <h3 className="fw-bold mb-1 d-flex align-items-center gap-2">
                                        <i className="bi bi-pencil-square text-primary"></i> Edit Envelope
                                    </h3>
                                    <p className="text-muted small mb-0">Changes here apply to your master budget blueprint.</p>
                                </div>

                                <Form onSubmit={handleUpdateBucket}>
                                    <Row className="g-4 mb-4">
                                        <Col md={8}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold text-muted text-header-caps">Bucket Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="lg"
                                                    className="fw-bold text-dark shadow-sm"
                                                    value={editForm.name}
                                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold text-muted text-header-caps">Master Target</Form.Label>
                                                <InputGroup size="lg" className="shadow-sm">
                                                    <InputGroup.Text className="bg-white text-muted">$</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        className="fw-bold"
                                                        value={editForm.goal}
                                                        onChange={e => setEditForm({ ...editForm, goal: parseFloat(e.target.value) || 0 })}
                                                        required
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* LOAN SPECIFIC FIELDS */}
                                    {selectedBucket.loan_details && (
                                        <div className="bg-warning bg-opacity-10 p-4 rounded-4 border border-warning border-opacity-25 mb-4">
                                            <h6 className="fw-bold text-dark text-uppercase letter-spacing-1 mb-3"><i className="bi bi-bank me-2"></i>Debt Parameters</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <Form.Label className="small fw-bold text-muted">Original Principal</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>$</InputGroup.Text>
                                                        <Form.Control type="number" step="0.01" value={editForm.loanPrincipal} onChange={e => setEditForm({ ...editForm, loanPrincipal: e.target.value })} />
                                                    </InputGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Label className="small fw-bold text-muted">Start Date</Form.Label>
                                                    <Form.Control type="date" value={editForm.loanStart} onChange={e => setEditForm({ ...editForm, loanStart: e.target.value })} />
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Label className="small fw-bold text-muted">Interest Rate (APR %)</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control type="number" step="0.01" value={editForm.loanInterest} onChange={e => setEditForm({ ...editForm, loanInterest: e.target.value })} />
                                                        <InputGroup.Text>%</InputGroup.Text>
                                                    </InputGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Label className="small fw-bold text-muted">Minimum Payment</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>$</InputGroup.Text>
                                                        <Form.Control type="number" step="0.01" value={editForm.loanMinPayment} onChange={e => setEditForm({ ...editForm, loanMinPayment: e.target.value })} />
                                                    </InputGroup>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
                                        <Form.Check
                                            type="switch"
                                            id="archive-switch"
                                            label={<span className="fw-bold text-danger ms-2">Archive Bucket</span>}
                                            checked={editForm.archived}
                                            onChange={e => setEditForm({ ...editForm, archived: e.target.checked })}
                                        />

                                        <Button type="submit" variant="primary" className="rounded-pill px-5 fw-bold shadow-sm">
                                            Save Changes
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-light rounded-4 border text-muted opacity-75">
                            <i className="bi bi-inboxes display-1 mb-3"></i>
                            <p className="fw-medium">Select a bucket from the list to edit its blueprint.</p>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );
}
